import { TransactionItemCheckIssue } from '@/types/transactions';
import { SVSummaryCard } from '../core';
import { SVSummaryCardItem } from '@/types/common';
import { formatDateTimeFromUTC } from '@/utils/date';
import TransactionAmount from './TransactionAmount';
import { buildAddress } from '@/utils/address';
import { useAuthUser } from '@/hooks/stores/useAuthUser';
import {
  getCheckDepositErrorReason,
  getTransactionDeliverySpeedFromTransferType,
} from '@/utils/transactions';

type CheckIssueTransactionDetailsProps = {
  transactionDetails: TransactionItemCheckIssue;
};

export default function CheckIssueTransactionDetails({
  transactionDetails,
}: CheckIssueTransactionDetailsProps) {
  const { isStaff } = useAuthUser();
  const {
    statusLabel,
    fromAccount,
    toAccount,
    id,
    transferType,
    note,
    description,
    instructions,
    issuedCheck,
  } = transactionDetails;
  const {
    canceledAt,
    clearedAt,
    expiredAt,
    returnedAt,
    sentAt,
    paymentStoppedAt,
    errorAt,
    issuedAt,
  } = issuedCheck;
  const { recipientAddress, recipientName, nickName } = toAccount || {};

  const items: SVSummaryCardItem[] = [
    {
      label: 'Amount',
      customContent: <TransactionAmount transaction={transactionDetails} />,
    },
    { label: 'Status', text: statusLabel },
    { label: 'Created At', text: formatDateTimeFromUTC(issuedAt, true) },
    { label: 'Canceled At', text: formatDateTimeFromUTC(canceledAt, true) },
    { label: 'Cleared At', text: formatDateTimeFromUTC(clearedAt, true) },
    { label: 'Expired At', text: formatDateTimeFromUTC(expiredAt, true) },
    { label: 'Returned At', text: formatDateTimeFromUTC(returnedAt, true) },
    { label: 'Sent At', text: formatDateTimeFromUTC(sentAt, true) },
    {
      label: 'Stopped At',
      text: formatDateTimeFromUTC(paymentStoppedAt, true),
    },
    {
      label: 'Error At',
      text: formatDateTimeFromUTC(errorAt, true),
    },
    {
      label: 'Error Reason',
      text: isStaff
        ? getCheckDepositErrorReason(transactionDetails.errorReason)
        : '',
    },
    {
      label: 'From Account',
      text: fromAccount?.bankName || '',
    },
    {
      label: 'To Account',
      text: `${nickName}\n${recipientName}\n`,
    },
    {
      label: 'To Address',
      text: recipientAddress
        ? buildAddress({
            line1: recipientAddress.addressLine1,
            line2: recipientAddress.addressLine2,
            city: recipientAddress.addressCity,
            state: recipientAddress.addressStateCode,
            postalCode: recipientAddress.addressPostalCode,
          })
        : '',
    },
    { label: 'Transaction Id', text: id },
    {
      label: 'Payment Type',
      text: getTransactionDeliverySpeedFromTransferType(transferType),
    },
    { label: 'Note', text: note || '' },
    { label: 'Transaction Description', text: description },
    { label: 'Instructions', text: instructions || '' },
  ];

  return <SVSummaryCard label="Basic Info" items={items} />;
}
